<script setup lang="ts">
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableBody,
  TableCell
} from '~/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip'
import { CurrencyIcon } from '~/components/ui/currency-icon'
import type { IExtendedRateCurrency } from '~/components/widget/rates'
import type { IProcessedRateBank } from '~/components/widget/rates/types'
import { RATE_PLACEHOLDER } from '~/data/pages/rates/consts'

const props = defineProps<{
  currency: keyof IExtendedRateCurrency
  action?: 'buy' | 'sell'
}>()

const { data } = await useFetch('/api/rates/organizations-cached')

const banks = computed(() => {
  return (
    data.value?.data.map((item) => {
      const rates: Record<string, { buy?: number; sell?: number }> = {}

      if (item.rates && typeof item.rates === 'object') {
        Object.entries(item.rates).forEach(([key, value]) => {
          const match = key.match(/^(buy|sell)_(\w+)$/)
          if (!match) return

          const [, operation, currency] = match
          const curr = currency.toLowerCase()

          if (!rates[curr]) {
            rates[curr] = {}
          }

          // Number(null) is 0, which would masquerade as a real rate.
          rates[curr][operation as 'buy' | 'sell'] =
            value === null || value === undefined || value === '' ? undefined : Number(value)
        })
      }

      return {
        id: item.id,
        title: item.title,
        logo: item.logo,
        url: item.url,
        time: useDateTimeFormat(item.created_at),
        rates
      }
    }) || []
  )
})

const filteredBanks = computed(() => {
  return banks.value.filter((bank) => {
    const rate = bank.rates[props.currency]
    return (
      rate &&
      ((rate.buy !== undefined && rate.buy > 0) || (rate.sell !== undefined && rate.sell > 0))
    )
  })
})

const bestRates = computed(() => {
  const rates = filteredBanks.value
    .map((bank) => bank.rates[props.currency])
    .filter((rate) => rate && (rate.buy !== undefined || rate.sell !== undefined))

  const buyRates = rates.map((rate) => rate.buy).filter((rate) => rate !== undefined) as number[]
  const sellRates = rates.map((rate) => rate.sell).filter((rate) => rate !== undefined) as number[]

  return {
    buy: buyRates.length > 0 ? Math.max(...buyRates) : undefined,
    sell: sellRates.length > 0 ? Math.min(...sellRates) : undefined
  }
})

const isBestRate = (bank: IProcessedRateBank, operation: 'buy' | 'sell') => {
  const rate = bank.rates[props.currency]?.[operation]
  if (rate === undefined || bestRates.value[operation] === undefined) return false

  return rate === bestRates.value[operation]
}
</script>

<template>
  <div>
    <Table
      class="whitespace-nowrap text-xs sm:text-base [&_td]:p-1 sm:[&_td]:p-4 [&_th]:p-1 sm:[&_th]:p-4"
    >
      <TableHeader>
        <TableRow class="!border-b-0">
          <TableHead />
          <TableHead :colspan="2" class="w-[160px] !p-0">
            <div class="flex items-center justify-center gap-2 font-bold uppercase text-gray-900">
              <CurrencyIcon :code="currency" size="lg" />{{ currency }}
            </div>
          </TableHead>
        </TableRow>
        <TableRow class="lowercase">
          <TableHead>{{ $t('common.title_column') }}</TableHead>

          <TableHead v-if="!props.action || props.action === 'buy'" class="w-[100px] text-center">
            {{ $t('common.buy') }}
          </TableHead>

          <TableHead v-if="!props.action || props.action === 'sell'" class="w-[100px] text-center">
            {{ $t('common.sell') }}
          </TableHead>

          <TableHead class="text-center">{{ $t('common.time') }}</TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        <TableRow v-for="bank in filteredBanks" :key="bank.id">
          <TableCell class="flex max-w-[125px] items-center gap-1 sm:max-w-none">
            <NuxtLinkLocale
              :to="bank.url"
              :aria-label="`${$t('common.more_about')} ${bank.title}`"
              class="flex w-full items-center gap-1 overflow-hidden font-semibold sm:gap-2"
            >
              <NuxtImg
                :src="bank.logo"
                :alt="`${$t('common.logo_alt')} ${bank.title}`"
                class="h-7 w-7 flex-shrink-0"
              />
              <span class="w-full truncate hover:underline sm:text-base">{{ bank.title }}</span>
            </NuxtLinkLocale>
          </TableCell>

          <TableCell
            v-if="!props.action || props.action === 'buy'"
            :class="[
              'w-[100px] text-center',
              isBestRate(bank, 'buy') ? 'bg-green-50' : 'bg-gray-100'
            ]"
          >
            {{ bank.rates[props.currency]?.buy ?? RATE_PLACEHOLDER }}
          </TableCell>

          <TableCell
            v-if="!props.action || props.action === 'sell'"
            :class="[
              'w-[100px] text-center',
              isBestRate(bank, 'sell') ? 'bg-green-50' : 'bg-gray-100'
            ]"
          >
            {{ bank.rates[props.currency]?.sell ?? RATE_PLACEHOLDER }}
          </TableCell>

          <TableCell class="w-[100px]">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger as-child>
                  <span class="block cursor-help text-center text-green-500">{{ bank.time }}</span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>
                    {{ $t('common.rate_actual') }}<br />{{ $t('common.set_at') }} {{ bank.time }}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
    <div class="mt-2 flex items-center justify-end gap-1 text-sm font-medium text-gray-500">
      <span class="mt-1 h-3 w-3 rounded-sm bg-green-200" />
      {{ $t('common.best_rate') }}
    </div>
  </div>
</template>
